import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/zustand/auth-store';
import { isClinicOwnerRole } from '@/utils/detect-user-type';
import { onboardingQueries } from '@/apis/onboarding/query';
import {
  getClinicOnboardingProgress,
  saveClinicOnboardingProgress,
  clearClinicOnboardingProgress,
} from '@/utils/clinic-onboarding-progress';

/**
 * Drives the Clinic Owner onboarding checklist on /clinic-setup:
 * - GET /onboarding/status is only ever called for the Clinic Owner role
 *   (any other role gets a 403 - no @Permissions guard on the backend, so the
 *   business-logic gate has to happen here).
 * - `nextStep: null` means there's nothing left to do - either there was
 *   never an active flow, or every step is now `completed: true` (`steps`
 *   itself stays a populated array in that case, it just has no more work
 *   left) - either way, bounce to /appointments instead of showing
 *   a checklist.
 * - Last known status is cached in localStorage so a reopened browser resumes
 *   on the right step instead of flashing step 1 while the refetch is in flight.
 */
export const useClinicOnboarding = ({ suppressRedirect = false } = {}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, accessToken } = useAuth();
  const isClinicOwner = isClinicOwnerRole(user?.role);

  const [cachedProgress] = useState(() => (isClinicOwner ? getClinicOnboardingProgress() : null));

  const { data, isLoading, isFetching, isError, error } = onboardingQueries.useOnboardingStatus({
    enabled: Boolean(accessToken) && isClinicOwner,
  });

  useEffect(() => {
    if (!isClinicOwner || !data || suppressRedirect) {
      return;
    }
    if (!data.nextStep) {
      clearClinicOnboardingProgress();
      navigate('/overview', { replace: true });
      return;
    }
    saveClinicOnboardingProgress(data);
  }, [data, isClinicOwner, navigate, suppressRedirect]);

  useEffect(() => {
    if (isClinicOwner && isError && error?.response?.status === 403) {
      clearClinicOnboardingProgress();
      navigate('/overview', { replace: true });
    }
  }, [isClinicOwner, isError, error, navigate]);

  const status = data ?? cachedProgress;

  const refetchStatus = () => queryClient.invalidateQueries({ queryKey: ['onboarding', 'status'] });

  return useMemo(
    () => ({
      isClinicOwner,
      isLoading: isClinicOwner && !status && (isLoading || isFetching),
      flow: status?.flow ?? null,
      steps: status?.steps ?? null,
      nextStep: status?.nextStep ?? null,
      refetchStatus,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isClinicOwner, status, isLoading, isFetching]
  );
};
